import { getDateTimeParts, getDaysInMonth, getPlaceholder, } from '@tesla/intl-date-time';
import { getDatePickerDate } from '../index.js';
export var DatePart;
(function (DatePart) {
    DatePart["Day"] = "day";
    DatePart["Month"] = "month";
    DatePart["Year"] = "year";
})(DatePart || (DatePart = {}));
export const datePartSpec = {
    [DatePart.Day]: {
        max: 31,
        min: 1,
    },
    [DatePart.Month]: {
        max: 12,
        min: 1,
    },
    [DatePart.Year]: {
        max: 9999,
        min: 1,
    },
};
const options = {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
};
// return placeholder based on locale
export const getLocalePlaceholder = (locale) => {
    return getPlaceholder(new Date(), locale, options);
};
// Parse dateParts and seperators from getDateTimeParts
// Required for getting the order of dateParts and seperators
// Returns the default placeholder
// Called when locale has changed
export const setLocaleDateParts = (locale, setValue = false) => {
    let placeholder = getLocalePlaceholder(locale);
    const parts = getDateTimeParts(new Date(), locale, options);
    let index = 0;
    let seperatorIndex = 0;
    const dateParts = [];
    // Parse position of each datePart and seperators between them
    for (let i = 0; i < parts.length; i++) {
        if (parts[i].type !== 'literal') {
            const datePartName = stringToDatePart(parts[i].type.toString());
            if (datePartName) {
                const cursorStart = index;
                const cursorEnd = (index += parts[i].value.length);
                const datePart = {
                    cursorEnd: cursorEnd,
                    cursorStart: cursorStart,
                    length: parts[i].value.length,
                    max: datePartSpec[datePartName].max,
                    min: datePartSpec[datePartName].min,
                    placeholder: '',
                    seperator: '',
                    type: datePartName,
                    value: setValue ? parts[i].value : null,
                };
                dateParts.push(datePart);
            }
        }
        else {
            const seperator = parts[i].value;
            dateParts[seperatorIndex++].seperator = seperator;
            index += seperator.length;
            placeholder = placeholder.replace(seperator, ',');
        }
    }
    const placeholderArray = placeholder.split(',');
    for (let i = 0; i < dateParts.length; i++) {
        dateParts[i].placeholder = placeholderArray[i];
    }
    return dateParts;
};
// Convert string to DatePart
// Called in getDateParts to parse string value from getDateTimeParts to datePart
export const stringToDatePart = (value) => {
    switch (value) {
        case 'day':
            return DatePart.Day;
        case 'month':
            return DatePart.Month;
        case 'year':
            return DatePart.Year;
    }
    return null;
};
// Convert datePart to string with leading zero
// Called in datePartsToString for converting a specific datePart
export const dateValueToString = (dateParts, position) => {
    let value = dateParts[position].value;
    if (value !== null)
        while (value.length < dateParts[position].placeholder.length)
            value = '0' + value;
    else
        value = dateParts[position].placeholder;
    return value;
};
// Convert date dateParts to String
// Called when setting a new displayValue with dateParts
export const datePartsToString = (dateParts) => {
    var _a;
    let output = '';
    for (let i = 0; i < dateParts.length; i++)
        output += dateValueToString(dateParts, i) + ((_a = dateParts[i].seperator) !== null && _a !== void 0 ? _a : '');
    return output;
};
export const getDatePart = (dateParts, datePart) => {
    for (let i = 0; i < dateParts.length; i++) {
        if (dateParts[i].type === datePart)
            return [dateParts[i], i];
    }
    return [undefined, 0];
};
// Convert date to dateParts
// Required for getting the dateParts when date is updated
export const dateToDateParts = (dateParts, date) => {
    if (date) {
        // If date is not in ISO date format will return current date
        date = typeof date === 'string' ? getDatePickerDate(date, 'string') : date;
        if (date && !isNaN(date.getTime())) {
            const [, dayIndex] = getDatePart(dateParts, DatePart.Day);
            const [, monthIndex] = getDatePart(dateParts, DatePart.Month);
            const [, yearIndex] = getDatePart(dateParts, DatePart.Year);
            dateParts[dayIndex].value = date.getDate().toString();
            dateParts[monthIndex].value = (date.getMonth() + 1).toString();
            dateParts[yearIndex].value = date.getFullYear().toString();
        }
    }
    return dateParts;
};
// Convert dateParts to date
// Called when updating the value
// Used onDateChange to parse the date value to the user
export const datePartsToDate = (dateParts) => {
    // Initialize with a random date to prevent messing up the date
    const output = new Date('2023-01-01T20:00:00Z');
    for (let i = 0; i < dateParts.length; i++) {
        const datePart = dateParts[i];
        if (datePart.value && parseInt(datePart.value) !== 0) {
            switch (datePart.type) {
                case DatePart.Day:
                    output.setDate(parseInt(datePart.value));
                    break;
                case DatePart.Month:
                    output.setMonth(parseInt(datePart.value) - 1);
                    break;
                case DatePart.Year:
                    output.setFullYear(parseInt(datePart.value));
                    break;
            }
        }
        else
            return null;
    }
    return output;
};
// Parse the selected datePart input, return null if not number within 1 to 9
// Called in handleChange to parse user input
export const getDatePartValue = (inputValue, position, dateParts) => {
    dateParts.forEach((datePart) => {
        if (datePart.seperator)
            inputValue = inputValue.replace(datePart.seperator, ',');
    });
    const value = parseInt(inputValue.split(',')[position].replace(/\D/g, ''));
    return Number.isNaN(value) ? null : value.toString();
};
// Return default datePart
// Called getUpdatedDatePart to get the default datePart value
export const getDefaultDate = (datePart, direction) => {
    const today = new Date();
    const month = today.getMonth() + 1;
    const year = today.getFullYear();
    switch (datePart) {
        case DatePart.Day:
            if (direction === 'ArrowUp') {
                return 1;
            }
            if (direction === 'ArrowDown') {
                return new Date(year, month, 0).getDate();
            }
            return today.getDate();
        case DatePart.Month:
            if (direction === 'ArrowUp') {
                return 1;
            }
            if (direction === 'ArrowDown') {
                return 12;
            }
            return month + 1;
        case DatePart.Year:
            return year;
    }
};
// Update dateParts to the boundary if they are within range
// When value is larger than max, limit it to max when cap is true, else the value becomes min
// Called when updating datePart value
export const getCorrectDatePartValue = (dateParts, value, position, cap) => {
    let year;
    let month;
    for (let i = 0; i < dateParts.length; i++) {
        if (dateParts[i].type === DatePart.Year)
            year = dateParts[i];
        if (dateParts[i].type === DatePart.Month)
            month = dateParts[i];
    }
    // Check last date of the month when year and month are given
    let max = dateParts[position].max;
    const min = dateParts[position].min;
    if (dateParts[position].type == DatePart.Day &&
        year !== undefined &&
        month !== undefined &&
        year.value !== null &&
        month.value !== null)
        max = getDaysInMonth(new Date(parseInt(year.value), parseInt(month.value) - 1));
    if (value > max)
        return cap ? max : min;
    if (value < min)
        return max;
    return value;
};
// Increase or decrease datePart value
// Use default value when datePart value is undefined
// Called in handleManualEntryOnClick to get updated datePart value
export const getUpdatedDatePart = (dateParts, position, value, direction) => {
    const datePartValueStr = dateParts[position].value;
    return (datePartValueStr === null
        ? getDefaultDate(dateParts[position].type, direction)
        : getCorrectDatePartValue(dateParts, parseInt(datePartValueStr) + value, position, false)).toString();
};
// Convert cursor position to matching datePart position
// Called when user clicks on the component to indentify the correct datePart to highlight
export const cursorToPosition = (dateParts, cursorPos) => {
    var _a;
    let i = 0;
    while (((_a = dateParts[i]) === null || _a === void 0 ? void 0 : _a.cursorEnd) < cursorPos)
        i++;
    return i;
};
// Change all zeros in dateParts to 1
// Called in handleZeroInDateParts to update 0s in dateParts to 1
export const fixZeroInDateParts = (dateParts) => {
    for (let i = 0; i < dateParts.length; i++) {
        const datePart = dateParts[i];
        if (datePart.value !== null && parseInt(datePart.value) === 0) {
            dateParts[i].value = '1';
        }
    }
    return dateParts;
};
// Check if day falls in the range based on year and month
export const checkDayRange = (dateParts) => {
    const [dayPart, dayIndex] = getDatePart(dateParts, DatePart.Day);
    if (dayPart !== undefined && dayPart.value !== undefined) {
        dayPart.value =
            dayPart.value != undefined
                ? getCorrectDatePartValue(dateParts, parseInt(dayPart.value), dayIndex, true).toString()
                : dayPart.value;
    }
    return [dayPart, dayIndex];
};
